import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies -- Dialog composes multiple invite components */
import { isDefined } from "@clipboard-health/util-ts";

import { Box } from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { type AddShiftQualificationOption } from "@src/appV2/redesign/AddShiftPanel/types";
import { type UseModalState } from "@src/lib/ui-react";
import { BottomSheet } from "../../../components/BottomSheet";
import { Dialog } from "../../../components/Dialog";
import { DialogHeader } from "../../../components/DialogHeader";
import { Text } from "../../../components/Text";
import { InviteContent } from "../../../DailyView/components/InviteDrawer/InviteContent";
import {
  type Professional,
  type ShiftDetails,
} from "../../../DailyView/components/InviteDrawer/types";
import { formatAgentRequestLabel } from "../../../DailyView/utils/formatAgentRequestLabel";
import { useChatInviteShiftDetails } from "./hooks/useChatInviteShiftDetails";
import { useChatWorkerQualification } from "./hooks/useChatWorkerQualification";

/* eslint-enable import/max-dependencies */

interface InviteToShiftDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  timezone: string;
  /** The worker's own qualification, used when the workplace has no matching qualifications. */
  workerQualification?: string;
  /** Qualifications the worker matches at this workplace. */
  matchingFacilityQualifications?: string[];
  /** The qualification from the chat channel's metadata (when opened from chat). */
  channelQualification?: string;
  /**
   * When provided, the invite starts with this professional already selected so
   * the user doesn't have to search for them.
   */
  preselectedProfessional?: Professional;
}

/**
 * Invite dialog (Dialog on desktop, BottomSheet on mobile) for inviting an
 * already-selected professional to a shift, rendering the unified
 * `InviteContent` with the same Role/Date/Type/Time fields the Add Shift
 * panel uses. `qualificationOptions` is restricted to the preselected
 * worker's `availableQualifications` rather than the full facility list,
 * since the invite is tied to that specific worker.
 */
export function InviteToShiftDialog(props: InviteToShiftDialogProps) {
  const { tu } = useTu("chat");
  const dialogTitle = tu("Invite to Shift");

  const {
    modalState,
    workplaceId,
    timezone,
    workerQualification,
    matchingFacilityQualifications,
    channelQualification,
    preselectedProfessional,
  } = props;

  const isMobile = useIsMobile();

  const { defaultQualification, availableQualifications } = useChatWorkerQualification({
    channelQualification,
    workerQualification,
    matchingFacilityQualifications,
  });

  const qualificationDisplayName = isDefined(defaultQualification)
    ? formatAgentRequestLabel(defaultQualification)
    : "";

  // Only seeds the initial shift (default date/slot + the workplace's default
  // times for it). Once `InviteContent` mounts, its own editable fields own
  // qualification/date/slot/time going forward — there's no picker header left
  // to keep in sync.
  const { shiftDetails, isLoading: isLoadingShiftTimes } = useChatInviteShiftDetails({
    workplaceId,
    qualification: defaultQualification ?? "",
    qualificationDisplayName,
    timezone,
  });

  if (!defaultQualification) {
    const emptyContent = (
      <Box sx={{ padding: 5, textAlign: "center" }}>
        <Text sx={(theme) => ({ color: theme.palette.base?.muted })} variant="body2">
          {tu("This professional cannot be invited to shifts at this workplace.")}
        </Text>
      </Box>
    );

    if (isMobile) {
      return (
        <BottomSheet modalState={modalState}>
          <DialogHeader title={dialogTitle} onClose={modalState.closeModal} />
          {emptyContent}
        </BottomSheet>
      );
    }

    return (
      <Dialog modalState={modalState} title={dialogTitle} width={480}>
        {emptyContent}
      </Dialog>
    );
  }

  const qualificationOptions: AddShiftQualificationOption[] = availableQualifications.map(
    (name) => ({ name, displayLabel: formatAgentRequestLabel(name) })
  );

  const content = (
    <InviteToShiftContent
      isLoadingShiftTimes={isLoadingShiftTimes}
      preselectedProfessional={preselectedProfessional}
      qualificationOptions={qualificationOptions}
      shiftDetails={shiftDetails}
      onSuccess={modalState.closeModal}
    />
  );

  if (isMobile) {
    return (
      <BottomSheet maxHeightPercent={95} modalState={modalState}>
        <DialogHeader title={dialogTitle} onClose={modalState.closeModal} />
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {content}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      disableBodyPadding
      modalState={modalState}
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "calc(100% - 64px)",
        },
      }}
      title={dialogTitle}
      width={480}
    >
      {content}
    </Dialog>
  );
}

interface InviteToShiftContentProps {
  isLoadingShiftTimes: boolean;
  preselectedProfessional?: Professional;
  qualificationOptions: AddShiftQualificationOption[];
  shiftDetails: ShiftDetails | undefined;
  onSuccess: () => void;
}

/**
 * Shared content for both Dialog and BottomSheet.
 */
function InviteToShiftContent(props: InviteToShiftContentProps) {
  const { tu } = useTu("chat");

  const {
    isLoadingShiftTimes,
    preselectedProfessional,
    qualificationOptions,
    shiftDetails,
    onSuccess,
  } = props;

  if (!isDefined(shiftDetails) || isLoadingShiftTimes) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 5,
        }}
      >
        <Text sx={(theme) => ({ color: theme.palette.base?.muted })} variant="body2">
          {tu("Loading shift times...")}
        </Text>
      </Box>
    );
  }

  return (
    <InviteContent
      preselectedProfessional={preselectedProfessional}
      qualificationOptions={qualificationOptions}
      shiftDetails={shiftDetails}
      onSuccess={onSuccess}
    />
  );
}
